import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import {
  ApiConfiguration,
  apiAuthResendVerificationEmailPost,
  apiAuthVerifyEmailPost,
  apiAuthRequestUnlockPost,
  apiAuthUnlockPost,
  apiAuthRequestPasswordResetPost,
  apiAuthResetPasswordPost,
} from 'amoxcalli-identity-api';
import { authFeature } from '../state/auth/auth.reducer';
import { AuthActions } from '../state/auth/auth.actions';
import { SILENT_REQUEST } from '../interceptors/http.interceptor';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);
  private readonly TOKEN_KEY = 'amxp_token';

  readonly currentUser = this.store.selectSignal(authFeature.selectUser);

  get token(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  logout(): void {
    this.store.dispatch(AuthActions.logout());
  }

  handlePostLogin(): void {
    this.router.navigate(['/padre/dashboard']);
  }

  async reenviarVerificacion(email: string): Promise<void> {
    await firstValueFrom(
      apiAuthResendVerificationEmailPost(this.http, this.apiConfig.rootUrl, { body: { email, app: 1 } })
    );
  }

  async verificarEmail(token: string): Promise<void> {
    await firstValueFrom(
      apiAuthVerifyEmailPost(this.http, this.apiConfig.rootUrl, { body: { token } })
    );
  }

  async reenviarDesbloqueo(email: string): Promise<void> {
    await firstValueFrom(
      apiAuthRequestUnlockPost(this.http, this.apiConfig.rootUrl, { body: { email, app: 1 } })
    );
  }

  async desbloquearCuenta(token: string): Promise<void> {
    await firstValueFrom(
      apiAuthUnlockPost(this.http, this.apiConfig.rootUrl, { body: { token } })
    );
  }

  async recuperar(email: string): Promise<void> {
    await firstValueFrom(
      apiAuthRequestPasswordResetPost(this.http, this.apiConfig.rootUrl, { body: { email, app: 1 } })
    );
  }

  // Silenciosa: el backend responde en inglés y la pantalla muestra su propio mensaje.
  // Un token inexistente (404) o usado/vencido (409) se reporta como vencido para que
  // la pantalla ofrezca pedir un enlace nuevo.
  async restablecer(token: string, newPassword: string): Promise<void> {
    try {
      await firstValueFrom(
        apiAuthResetPasswordPost(
          this.http,
          this.apiConfig.rootUrl,
          { body: { token, newPassword } },
          new HttpContext().set(SILENT_REQUEST, true)
        )
      );
    } catch (err: unknown) {
      if (err instanceof HttpErrorResponse && (err.status === 404 || err.status === 409)) {
        throw new Error('Token expired');
      }
      throw err;
    }
  }
}
